/* eslint-disable simple-import-sort/imports */
import React from 'react';
import { keyframes, styled } from 'styled-components';

import foxImage from 'assets/img/goodie-fox.webp';
import { PrimaryButton } from 'pages/HomeV2/components/Buttons';
import { Reveal } from 'pages/HomeV2/components/Motion';
import { CALENDLY_URL, Container, Section, v2 } from 'pages/HomeV2/v2';

import { GOODIE_EVENTS, trackGoodieEvent } from '../tracking';

const Wrapper = styled(Section)`
  background: ${v2.bgSoft};
  border-top: 1px solid ${v2.border};
  overflow: hidden;
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

/* Same rotating-gradient panel as the contact section on the start page,
   with the orange weighted a little stronger for the chocolate moment. */
const Panel = styled.div`
  position: relative;
  border-radius: 32px;
  overflow: hidden;
  padding: clamp(36px, 6vw, 80px);
  border: 1px solid ${v2.borderStrong};
  background: ${v2.bgElevated};
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: -60%;
    background: conic-gradient(
      from 0deg,
      rgba(255, 159, 79, 0.34),
      rgba(30, 150, 252, 0.3),
      rgba(255, 159, 79, 0.06),
      rgba(255, 159, 79, 0.34)
    );
    animation: ${rotate} 18s linear infinite;
    z-index: -2;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  &::after {
    content: '';
    position: absolute;
    inset: 1.5px;
    border-radius: 31px;
    background: radial-gradient(
        90% 110% at 50% 0%,
        rgba(255, 159, 79, 0.14),
        transparent 60%
      ),
      ${v2.bgElevated};
    z-index: -1;
  }

  @media (max-width: 480px) {
    border-radius: 24px;

    &::after {
      border-radius: 23px;
    }
  }
`;

const PanelGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  align-items: center;
  text-align: center;

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1.25fr) minmax(0, 0.75fr);
    gap: clamp(32px, 5vw, 64px);
    text-align: left;
  }
`;

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;

  @media (min-width: 900px) {
    align-items: flex-start;
  }
`;

const Heading = styled.h2`
  font-family: ${v2.fontDisplay};
  font-size: clamp(1.9rem, 4vw, 3.1rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.08;
  color: ${v2.text};
  margin: 0;
  text-wrap: balance;

  .grad {
    background: ${v2.gradient};
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const SubText = styled.p`
  font-family: ${v2.fontBody};
  font-size: clamp(1.02rem, 1.4vw, 1.14rem);
  line-height: 1.65;
  color: ${v2.textMuted};
  margin: 0;
  max-width: 480px;
`;

const CtaRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
  width: 100%;

  @media (max-width: 480px) {
    a {
      width: 100%;
    }
  }

  @media (min-width: 900px) {
    align-items: flex-start;
  }
`;

const Small = styled.p`
  margin: 0;
  font-family: ${v2.fontBody};
  font-size: 13.5px;
  color: ${v2.textFaint};
`;

const ContactInfo = styled.p`
  margin: 8px 0 0;
  font-family: ${v2.fontBody};
  font-size: 14px;
  color: ${v2.textFaint};

  a {
    color: ${v2.textMuted};
    font-weight: 600;
    border-bottom: 1px solid ${v2.border};
    transition:
      color 0.25s ease,
      border-color 0.25s ease;

    &:hover {
      color: ${v2.text};
      border-color: ${v2.text};
    }
  }
`;

const FoxStage = styled.div`
  position: relative;
  display: none;
  align-self: end;
  height: 340px;
  margin-bottom: calc(-1 * clamp(36px, 6vw, 80px));

  @media (min-width: 900px) {
    display: block;
  }
`;

const FoxGlow = styled.div`
  position: absolute;
  left: 50%;
  bottom: 10%;
  width: 90%;
  aspect-ratio: 1;
  transform: translateX(-50%);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 159, 79, 0.3), transparent 62%);
  filter: blur(40px);
  pointer-events: none;
`;

const Fox = styled.img`
  position: absolute;
  left: 50%;
  bottom: 0;
  height: 112%;
  width: auto;
  max-width: none;
  transform: translateX(-50%);
  filter: drop-shadow(0 20px 36px rgba(0, 0, 0, 0.45));
  -webkit-mask-image: linear-gradient(to bottom, #000 86%, transparent 100%);
  mask-image: linear-gradient(to bottom, #000 86%, transparent 100%);
  user-select: none;
  -webkit-user-drag: none;
`;

const GoodieCta = () => (
  <Wrapper id="demo" aria-labelledby="goodie-cta-title">
    <Container>
      <Reveal>
        <Panel>
          <PanelGrid>
            <Copy>
              <Heading id="goodie-cta-title">
                Hat die Schokolade überzeugt? Dann geben Sie{' '}
                <span className="grad">FOXI</span> auch eine Chance.{' '}
                <span aria-hidden>🦊</span>
              </Heading>
              <SubText>
                Wir zeigen Ihnen FOXI gerne persönlich und unverbindlich.
              </SubText>
              <CtaRow>
                <PrimaryButton
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() =>
                    trackGoodieEvent(GOODIE_EVENTS.demoClick, {
                      placement: 'final_cta',
                    })
                  }
                >
                  Kostenlose Demo buchen
                </PrimaryButton>
                <Small>
                  Dauert nur wenige Minuten und ist natürlich unverbindlich.
                </Small>
              </CtaRow>
              <ContactInfo>
                Oder direkt per E-Mail:{' '}
                <a href="mailto:info@foxi-abrechnung.de">
                  info@foxi-abrechnung.de
                </a>
              </ContactInfo>
            </Copy>

            <FoxStage aria-hidden>
              <FoxGlow />
              <Fox src={foxImage} alt="" loading="lazy" decoding="async" />
            </FoxStage>
          </PanelGrid>
        </Panel>
      </Reveal>
    </Container>
  </Wrapper>
);

export default GoodieCta;
